import React, { useState, useEffect } from 'react';
import Slider from 'rc-slider';
import 'rc-slider/assets/index.css';
import { Modal, Button } from 'react-bootstrap';
import { API } from '../../data/API/API';
import './ThresholdSlider.scss';

interface ThresholdSliderProps {
    orgNr: string;
    initialValues: [number, number];
    onClose: () => void;
    isOpen: boolean;
    updateThresholdValues: (newValues: { LimitYellow: number, LimitRed: number }) => void;
}

const ThresholdSlider: React.FC<ThresholdSliderProps> = ({ orgNr, initialValues, onClose, isOpen, updateThresholdValues }) => {
    const [liveSliderValues, setLiveSliderValues] = useState<[number, number]>(initialValues);
    const [saveStatus, setSaveStatus] = useState<string | null>(null);
    const [hasChanged, setHasChanged] = useState(false);  // Track if values have changed

    useEffect(() => {
        if (isOpen) {
            setLiveSliderValues(initialValues); // Reset liveSliderValues to initialValues when modal is opened
            setSaveStatus(null); // Reset save status when modal is opened
            setHasChanged(false); // Reset the change tracker
        }
    }, [isOpen, initialValues]);

    const handleSliderChange = (value: number | number[]) => {
        if (Array.isArray(value)) {
            setLiveSliderValues(value as [number, number]); // Immediate update for visual feedback
            setHasChanged(true); // Mark that values have changed
        }
    };

    const saveSliderValues = async () => {
        try {
            const response = await API.Suppliers.PostThresholdValues(orgNr, liveSliderValues[0], liveSliderValues[1]);

            if (response === 'Threshold saved successfully.') {
                setSaveStatus('Gränsvärden sparades!');
                // Update the parent component with the new values
                updateThresholdValues({
                    LimitYellow: liveSliderValues[0],
                    LimitRed: liveSliderValues[1],
                });
                setTimeout(() => {
                    onClose(); // Delay the closing to ensure save operation is visible
                    setSaveStatus(''); // Reset save status after closing
                }, 2000); // Close after 500ms
            } else {
                setSaveStatus('Misslyckades med att spara gränsvärden.');
            }
        } catch (error) {
            setSaveStatus('Fel vid sparande av gränsvärden.');
        }
    };

    return (
        <Modal show={isOpen} onHide={onClose} centered>
            <Modal.Header closeButton style={{backgroundColor:'#173c53',color:'white'}}>
                <Modal.Title>Ange gränsvärden</Modal.Title>
            </Modal.Header>
            <Modal.Body style={{backgroundColor:'#fffaf5'}}>
                {/* Add a Swedish description below the header */}
                <p className="slider-description">
                    Med detta verktyg kan du justera gränserna för A.Värde (Avvikande Värde) för att individuellt anpassa vad som ska visas som gult eller rött för varje leverantör. Detta hjälper dig att undvika varningar för leverantörer som ofta når högre värden och istället fokusera på avvikelser som verkligen kräver åtgärd.
                </p>

                <Slider
                    range
                    min={0}
                    max={100}
                    step={1}
                    value={liveSliderValues}
                    onChange={handleSliderChange}
                    marks={{ 0: '0%', 100: '100%' }}
                    handleStyle={[
                        { backgroundColor: '#F5A32D', borderColor: '#F5A32D' }, // Yellow for the left handle
                        { backgroundColor: '#DC143C', borderColor: '#DC143C' }  // Red for the right handle
                    ]}
                    trackStyle={[
                        { backgroundColor: '#f8b133' } // Custom color for the track between the handles
                    ]}
                    railStyle={{
                        background: `linear-gradient(
                    to right, 
                    #07A235 0%, 
                    #07A235 ${liveSliderValues[0]}%, 
                    #F5A32D ${liveSliderValues[0]}%, 
                    #F5A32D ${liveSliderValues[1]}%, 
                    #DC143C ${liveSliderValues[1]}%, 
                    #DC143C 100%
                )`
                    }}
                />
                <div className="threshold-circles">
                    <div className="circle yellow">
                        <p>{liveSliderValues[0]}%</p>
                    </div>
                    <div className="circle red">
                        <p>{liveSliderValues[1]}%</p>
                    </div>
                </div>
                {saveStatus && (
                    <p style={{
                        color: saveStatus.includes('sparades') ? 'green' : 'red',
                        marginTop: '10px',
                        textAlign: 'center'
                    }}>
                        {saveStatus}
                    </p>
                )}
            </Modal.Body>
            <Modal.Footer style={{backgroundColor:'#fffaf5'}}>
                <button className="btn button-cancel" onClick={onClose}>Avbryt</button>
                <button className="btn" onClick={saveSliderValues} disabled={!hasChanged}> {/* Disable if values haven't changed */}
                    Spara ändringar
                </button>
            </Modal.Footer>
        </Modal>
    );
};

export default React.memo(ThresholdSlider);
